import React, { useEffect } from 'react';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CBreadcrumb,
  CBreadcrumbItem,
  CBadge,
  CAvatar,
} from '@coreui/react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import UsersInputs from '../../components/UsersInputs/UsersInputs';
import { connect } from 'react-redux';
import { useTranslation } from '../../hooks/useTranslation';
import {
  updateUserRequestAction,
} from '../../store/users/usersActions';
import { CIcon } from '@coreui/icons-react';
import { cilUser } from '@coreui/icons';
// import { formatDateOnly } from '../../helpers/dateHelper';

const UpdateUser = (props) => {
  const {
    updateUserRequest,
    isLoading,
    updateUserData,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { t } = useTranslation();

  const userData = location.state?.user || null;

  useEffect(() => {
    if (!userData && id) {
      console.warn('No user data found in route state. You may need to fetch by ID:', id);
    }
  }, [userData, id]);

  const handleUserSubmit = (userFormData) => {
    console.log('Submitting updated user:', userFormData);
    
    updateUserRequest({
      data: userFormData,
      navigate: navigate
    });
  };

  const handleBackToUsers = () => {
    navigate('/users');
  };

  const getUserStatusBadge = (status) => {
    const statusMap = {
      1: { color: 'success', text: t('activeUser') || 'Aktif' },
      0: { color: 'secondary', text: t('inactiveUser') || 'İnaktif' }
    };
    
    const statusInfo = statusMap[status] || { color: 'secondary', text: status };
    return <CBadge color={statusInfo.color} shape="rounded-pill">{statusInfo.text}</CBadge>;
  };

  const formatDate = (dateTime) => {
    if (!dateTime || dateTime === "0") return '-';
    
    const dateStr = String(dateTime);
    if (dateStr.length === 14) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const minute = dateStr.substring(10, 12);
      const second = dateStr.substring(12, 14);
      
      const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
      
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
    
    return '-';
  };

  const getFullName = (user) => {
    if (!user) return '';
    const name = user.Name || '';
    const surname = user.Surname || '';
    return `${name} ${surname}`.trim() || '-';
  };

  return (
    <div>
      <CContainer>
        <CBreadcrumb className="mb-4">
          <CBreadcrumbItem 
            href="#/users" 
            onClick={(e) => {
              e.preventDefault();
              handleBackToUsers();
            }}
          >
            {t('users') || 'Kullanıcılar'}
          </CBreadcrumbItem>
          <CBreadcrumbItem active>
            {t('updateUser') || 'Kullanıcı Güncelle'}
          </CBreadcrumbItem>
        </CBreadcrumb>

        <CRow>
          <CCol xs={12}>
            <CCard>
              <CCardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h4 className="mb-0 me-3">
                      {t('updateUser') || 'Kullanıcı Güncelle'}
                    </h4>
                    {userData && (
                      <div className="d-flex align-items-center gap-3">
                        <CAvatar 
                          color="primary" 
                          textColor="white"
                          size="sm"
                        >
                          {userData.Name?.charAt(0)?.toUpperCase() 
                          || <CIcon icon={cilUser} />}
                        </CAvatar>
                        <div>
                          <div className="fw-semibold">{getFullName(userData)}</div>
                          <small className="text-muted">@{userData.UserName}</small>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {userData?.UserId && (
                    <div className="text-end">
                      <small className="text-muted d-block">
                        ID: {userData.UserId}
                      </small>
                      {userData.LastLoginDatetime && (
                        <small className="text-muted d-block">
                          {t('userLastLogin') || 'Son Giriş'}: 
                          {formatDate(userData.LastLoginDatetime)}
                        </small>
                      )}
                    </div>
                  )}
                </div>
                
                {userData && (
                  <div className="d-flex gap-2 mt-3">
                    {getUserStatusBadge(userData.Status)}
                    {userData.Email && (
                      <CBadge color="light" className="text-dark">
                        {userData.Email}
                      </CBadge>
                    )}
                  </div>
                )}
              </CCardHeader>
              
              <CCardBody>
                {userData ? (
                  <UsersInputs 
                    onSubmit={handleUserSubmit}
                    onCancel={handleBackToUsers}
                    isLoading={isLoading}
                    submitResponse={updateUserData}
                    initialData={userData}
                    isEdit={true}
                  />
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <CIcon icon={cilUser} size="3xl" className="text-muted" />
                    </div>
                    <div className="text-muted mb-3">
                      <h5>{t('noDataFound') || 'Veri Bulunamadı'}</h5>
                      <p>Kullanıcı bilgileri bulunamadı veya yüklenemedi.</p>
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={handleBackToUsers}
                    >
                      {t('backToUsers') || 'Kullanıcılara Dön'}
                    </button>
                  </div>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
        
        {/* User Info Summary Card - Only show when editing existing user */}
        {userData && (
          <CRow className="mt-4">
            <CCol xs={12}>
              <CCard className="border-start border-primary border-4">
                <CCardBody className="py-3">
                  <div className="row">
                    <div className="col-md-3">
                      <strong>{t('userEmail') || 'E-posta'}:</strong><br/>
                      <span className="text-muted">{userData.Email || '-'}</span>
                    </div>
                    <div className="col-md-3">
                      <strong>{t('mobilePhone') || 'Cep Telefonu'}:</strong><br/>
                      <span className="text-muted">{userData.CepTel || '-'}</span>
                    </div>
                    <div className="col-md-3">
                      <strong>{t('phone') || 'Telefon'}:</strong><br/>
                      <span className="text-muted">{userData.Telefon || '-'}</span>
                    </div>
                    <div className="col-md-3">
                      <strong>{t('userStatus') || 'Durum'}:</strong><br/>
                      {getUserStatusBadge(userData.Status)}
                    </div>
                  </div>
                  
                  {userData.InsertDatetime && userData.InsertDatetime !== "0" && (
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <strong>{t('createdDate') || 'Oluşturulma Tarihi'}:</strong><br/>
                        <span className="text-muted">{formatDate(userData.InsertDatetime)}</span>
                      </div>
                      <div className="col-md-6">
                        <strong>{t('updatedDate') || 'Güncellenme Tarihi'}:</strong><br/>
                        <span className="text-muted">{formatDate(userData.UpdateDatetime)}</span>
                      </div>
                    </div>
                  )}
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        )}
      </CContainer>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    isLoading: state.loadingReducer?.isLoading || false,
    updateUserData: state.usersReducer?.updateUserData || null,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUserRequest: (payload) => {
      dispatch(updateUserRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateUser);